import { createFileRoute, useNavigate, Link, useSearch, redirect } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { useServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import {
  buildEmailConfirmationRedirect,
  safeInternalPath,
  saveSignupResume,
} from "@/lib/signup-resume";
import { setAuthLoading } from "@/components/AuthLoadingOverlay";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { setBartenderLocation } from "@/lib/profile.functions";
import { recordTosAcceptance } from "@/lib/tos.functions";
import { logCtaRedirect, pickCtaContext, type CtaContext } from "@/lib/cta-analytics";
import { resolveCtaToken, consumeCtaToken, getCtaShiftPreview, getOnboardingDestination } from "@/lib/cta.functions";
import { applyReferralCode } from "@/lib/referrals.functions";
import { MARKETING_TAGLINE } from "@/lib/brand";

export const Route = createFileRoute("/auth")({
  head: () => ({
    meta: [
      { title: "Sign in or create an account — The Hospitality Reserve" },
      { name: "description", content: `Sign in or create a free account on The Hospitality Reserve. ${MARKETING_TAGLINE}` },
      { property: "og:title", content: "Sign in or create an account — The Hospitality Reserve" },
      { property: "og:url", content: "https://thehospitalityreserve.com/auth" },
    ],
    links: [{ rel: "canonical", href: "https://thehospitalityreserve.com/auth" }],
  }),
  validateSearch: z.object({
    mode: z.enum(["signin", "signup"]).optional(),
    role: z.enum(["bartender", "venue"]).optional(),
    // CTA / campaign context — `ct` is the signed token (preferred), the
    // raw fields stay supported as a fallback (admin previews, manual links).
    ct: z.string().max(64).optional(),
    campaign: z.string().max(64).optional(),
    shift: z.string().uuid().optional(),
    postcode: z.string().max(16).optional(),
    radius: z.coerce.number().min(1).max(200).optional(),
    ref: z.string().max(64).optional(),
    destination: z.string().max(128).optional(),
    // Legacy suspended-user guard redirect target. We now redirect to the
    // dedicated account-suspended page, but keep these params for old links.
    suspended: z.string().max(4).optional(),
    reason: z.string().max(240).optional(),
    // Same-origin relative path to return to after auth completes (used by
    // the MCP OAuth consent flow so users come back to /.lovable/oauth/consent
    // with the pending authorization_id intact).
    next: z.string().max(512).optional(),
  }),
  beforeLoad: ({ search }) => {
    if (search.suspended === "1") {
      throw redirect({
        to: "/account-suspended",
        search: { reason: search.reason },
      });
    }
  },
  component: AuthPage,
});

function AuthPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/auth" });
  const { mode: searchMode } = search;
  const initialCtx = pickCtaContext(search as Record<string, unknown>);
  const saveLocation = useServerFn(setBartenderLocation);
  const recordTos = useServerFn(recordTosAcceptance);
  const resolveToken = useServerFn(resolveCtaToken);
  const consumeToken = useServerFn(consumeCtaToken);
  const fetchShiftPreview = useServerFn(getCtaShiftPreview);
  const fetchDestination = useServerFn(getOnboardingDestination);
  const [ctaContext, setCtaContext] = useState<CtaContext>(initialCtx);
  const [mode, setMode] = useState<"signin" | "signup">(searchMode ?? "signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  
  const [fullName, setFullName] = useState("");
  const [role, setRole] = useState<"bartender" | "venue">(search.role ?? "bartender");
  const [postcode, setPostcode] = useState(initialCtx.postcode ?? "");
  const [radius, setRadius] = useState<number>(initialCtx.radius ?? 15);
  const [tosAccepted, setTosAccepted] = useState(false);
  const [referralCodeInput, setReferralCodeInput] = useState(
    typeof search.ref === "string" && /^[A-Z0-9]{4,12}$/i.test(search.ref) ? search.ref.toUpperCase() : "",
  );
  const applyRefCode = useServerFn(applyReferralCode);
  const [shiftPreview, setShiftPreview] = useState<{
    role_type: string;
    start_time: string;
    address: string | null;
    postcode: string | null;
    hourly_rate: number;
  } | null>(null);
  const [tokenIssue, setTokenIssue] = useState<null | "expired_token" | "invalid_token">(null);
  const [loading, setLoading] = useState(false);
  // Render the form immediately; do the session check in the background so
  // mobile users never see a blocking "One moment…" splash before they can
  // start typing. If a session does turn up we'll navigate away.

  useEffect(() => {
    let active = true;
    (async () => {
      // 1) If a signed token came in, resolve it server-side. The server
      // sets an httpOnly signed cookie so context survives even if the URL
      // is rewritten or the user opens the form in a new tab.
      let ctx: CtaContext = initialCtx;
      if (initialCtx.token) {
        try {
          const res = await resolveToken({ data: { token: initialCtx.token } });
          if (res.ok && res.context) {
            ctx = {
              token: res.context.token,
              campaign: res.context.campaign ?? initialCtx.campaign,
              shift: res.context.shift_id ?? initialCtx.shift,
              postcode: res.context.postcode ?? initialCtx.postcode,
              radius: res.context.radius_km ?? initialCtx.radius,
              ref: res.context.ref ?? initialCtx.ref,
              // destination is a client-side hint; the DB token doesn't store it,
              // so preserve whatever came in on the URL.
              destination: initialCtx.destination,
            };
          } else if (!res.ok && active) {
            // Token bad/expired — strip it but keep any campaign context
            // that came in raw on the URL so we can offer a graceful restart.
            setTokenIssue(res.reason);
            ctx = { ...initialCtx, token: undefined };
            logCtaRedirect({
              stage: "auth",
              from: "/auth",
              to: "/auth",
              reason: `token:${res.reason}`,
              context: ctx,
            });
          }
        } catch (err) {
          console.warn("[cta] resolveCtaToken failed", err);
        }
      }
      if (!active) return;
      setCtaContext(ctx);
      if (ctx.postcode) setPostcode(ctx.postcode);
      if (ctx.radius) setRadius(ctx.radius);

      // 2) Already signed in? Hand off to /dashboard immediately.
      const { data: userRes } = await supabase.auth.getUser();
      if (!active) return;
      if (userRes.user) {
        if (ctx.token) {
          try { await consumeToken({ data: { token: ctx.token } }); } catch { /* ignore */ }
        }
        logCtaRedirect({
          stage: "auth",
          from: "/auth",
          to: "/dashboard",
          reason: "already-authenticated",
          user_id: userRes.user.id,
          context: ctx,
        });
        navigate({ to: "/dashboard", replace: true });
        return;
      }

      logCtaRedirect({
        stage: "auth",
        from: "/auth",
        to: "/auth",
        reason: "show-form",
        context: ctx,
      });
      

      // 3) If we know the shift id, ask the server for a SAFE preview so
      // we can show "you're registering for: …" without leaking other
      // venues' data. Definer function only returns OPEN, future shifts
      // and never reveals venue_id or PII.
      if (ctx.shift) {
        try {
          const res = await fetchShiftPreview({ data: { shift_id: ctx.shift } });
          if (active && res.ok && res.shift) {
            setShiftPreview({
              role_type: res.shift.role_type,
              start_time: res.shift.start_time,
              address: res.shift.address,
              postcode: res.shift.postcode,
              hourly_rate: Number(res.shift.hourly_rate),
            });
          }
        } catch { /* ignore */ }
      }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (mode === "signup" && !tosAccepted) {
      toast.error("Please accept the Terms and Privacy Policy to continue.");
      return;
    }
    setLoading(true);
    // Show the global overlay for the WHOLE post-credentials flow (signup
    // + profile save + token consume + destination resolve + navigate) so
    // the user never sees the form, the /dashboard hop, or any
    // _authenticated route-level placeholder flash through.
    setAuthLoading(true);
    try {
      if (mode === "signup") {
        // Remember exactly where this person was heading so confirming their
        // email returns them here instead of the landing page.
        const resumeNext =
          safeInternalPath(search.next) ??
          "/dashboard";
        saveSignupResume({ email, role, path: resumeNext });

        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: buildEmailConfirmationRedirect(window.location.origin, resumeNext),
            data: {
              full_name: fullName,
              role,
              ...(role === "bartender" && postcode.trim()
                ? { postcode: postcode.trim().toUpperCase(), travel_radius_km: radius }
                : {}),
            },
          },
        });
        if (error) throw error;


        // Timestamped Terms of Service acceptance. Fire-and-forget so an
        // audit-log hiccup never blocks sign-up, but logged server-side.
        try {
          await recordTos({ data: { role, context: "signup" } });
        } catch (tosErr) {
          console.warn("[tos] record acceptance failed", tosErr);
        }

        // Save bartender's home postcode + travel radius so they receive
        // alerts when nearby engagements are posted.
        if (role === "bartender") {
          try {
            await saveLocation({ data: { postcode, travel_radius_km: radius } });
          } catch (locErr) {
            const m = locErr instanceof Error ? locErr.message : "Could not save your postcode.";
            toast.error(m);
          }
        }

        toast.success("Almost there — check your email to confirm your account.");
        if (referralCodeInput.trim()) {
          try {
            const r = await applyRefCode({ data: { code: referralCodeInput.trim() } });
            if (r.ok) toast.success("Referral applied. Rewards unlock after your first completed shift.");
          } catch { /* non-blocking */ }
        }
        if (ctaContext.token) {
          try { await consumeToken({ data: { token: ctaContext.token } }); } catch { /* ignore */ }
        }
        logCtaRedirect({
          stage: "auth",
          from: "/auth",
          to: "/verify-email",
          reason: "signup-complete",
          context: ctaContext,
        });
        const safeNext = typeof search.next === "string" && /^\/[^/].*/.test(search.next) && !search.next.startsWith("//") ? search.next : null;
        if (safeNext) {
          window.location.assign(safeNext);
          return;
        }
        navigate({ to: "/verify-email", replace: true });
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        // Record device + fire new-device alert if unrecognised. Non-blocking.
        try {
          const { recordLoginDevice } = await import("@/lib/security-account.functions");
          const { getDeviceFingerprint } = await import("@/lib/device-fingerprint");
          void recordLoginDevice({ data: { fingerprint: getDeviceFingerprint() } }).catch(() => {});
        } catch { /* ignore */ }
        // Geocode a sign-up postcode that couldn't be resolved before the
        // email was confirmed. Non-blocking.
        try {
          const { ensureMyLocationGeocoded } = await import("@/lib/profile.functions");
          void ensureMyLocationGeocoded().catch(() => {});
        } catch { /* ignore */ }
        toast.success("Welcome back", {
          description: "You're signed in — taking you to your dashboard.",
          duration: 2500,
        });

        const consumePromise: Promise<unknown> = ctaContext.token
          ? consumeToken({ data: { token: ctaContext.token } }).catch(() => undefined)
          : Promise.resolve();
        const destPromise = fetchDestination().catch(() => null);
        const [, destRes] = await Promise.all([consumePromise, destPromise]);
        const safeNext = typeof search.next === "string" && /^\/[^/].*/.test(search.next) && !search.next.startsWith("//") ? search.next : null;
        const dest: string = safeNext ?? destRes?.to ?? "/dashboard";
        logCtaRedirect({
          stage: "auth",
          from: "/auth",
          to: dest,
          reason: "signin-complete",
          context: ctaContext,
        });
        if (safeNext) {
          window.location.assign(safeNext);
        } else {
          navigate({ to: dest as never, replace: true });
        }
        // Clear the overlay shortly after navigation kicks off. The
        // destination route's own pending UI / data loaders take over from
        // here; leaving the overlay on indefinitely was causing a frozen
        // splash after login on slow connections.
        window.setTimeout(() => setAuthLoading(false), 400);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Something went wrong.";
      toast.error(msg);
      setAuthLoading(false);
    } finally {
      setLoading(false);
    }
  };





  if (tokenIssue) {
    const preserved: Array<{ label: string; value: string }> = [];
    if (ctaContext.campaign) preserved.push({ label: "Campaign", value: ctaContext.campaign });
    if (ctaContext.shift) preserved.push({ label: "Shift", value: ctaContext.shift.slice(0, 8) + "…" });
    if (ctaContext.postcode) preserved.push({ label: "Postcode", value: ctaContext.postcode });
    if (typeof ctaContext.radius === "number") preserved.push({ label: "Radius", value: `${ctaContext.radius} km` });
    if (ctaContext.destination) preserved.push({ label: "Destination", value: ctaContext.destination });
    if (ctaContext.ref) preserved.push({ label: "Source", value: ctaContext.ref });
    const hasContext = preserved.length > 0;

    // Build a search object that strips only the bad token, preserving every
    // other CTA field so a refresh/share of /auth still carries the context.
    const restartSearch = {
      mode: "signup" as const,
      campaign: ctaContext.campaign,
      shift: ctaContext.shift,
      postcode: ctaContext.postcode,
      radius: ctaContext.radius,
      ref: ctaContext.ref,
      destination: ctaContext.destination,
    };

    return (
      <div className="min-h-dvh bg-background text-foreground flex flex-col">
        <header className="px-8 py-6 lg:px-16">
          <Link to="/" className="inline-block">
            <p className="tracking-wide-luxury text-accent">The Hospitality</p>
            <p className="font-display text-lg">Reserve</p>
          </Link>
        </header>
        <main className="flex flex-1 items-center justify-center px-6 py-12">
          <div className="w-full max-w-md text-center">
            <p className="tracking-wide-luxury text-accent">
              {tokenIssue === "expired_token" ? "This link has expired" : "This link is no longer valid"}
            </p>
            <span className="gold-divider mt-6" />
            <h1 className="mt-6 font-display text-3xl">No trouble at all.</h1>
            <p className="mt-3 text-sm text-muted-foreground">
              {tokenIssue === "expired_token"
                ? "Your link has timed out, but you can still create your account."
                : "We couldn't verify that link, but you can still create your account."}
              {hasContext && <> We've kept your details so nothing is lost.</>}
            </p>

            {hasContext && (
              <dl className="mt-6 border border-accent/30 bg-accent/5 p-4 text-left text-xs">
                {preserved.map((row) => (
                  <div key={row.label} className="flex justify-between gap-4 py-1">
                    <dt className="tracking-wide-luxury text-accent">{row.label}</dt>
                    <dd className="text-muted-foreground break-all">{row.value}</dd>
                  </div>
                ))}
              </dl>
            )}

            <div className="mt-8 flex flex-col gap-3">
              <Button
                type="button"
                onClick={() => {
                  logCtaRedirect({
                    stage: "auth",
                    from: "/auth",
                    to: ctaContext.destination ?? "/auth",
                    reason: "token-fallback:restart",
                    context: ctaContext,
                  });
                  setTokenIssue(null);
                  setMode("signup");
                  // Re-write the URL so every preserved field survives a refresh
                  // or share, even though the bad token is dropped.
                  navigate({ to: "/auth", search: restartSearch, replace: true });
                }}
                className="w-full rounded-none bg-foreground text-background hover:bg-foreground/90 tracking-wide-luxury py-6"
              >
                Create account
              </Button>
              <button
                type="button"
                onClick={() => {
                  setTokenIssue(null);
                  setMode("signin");
                }}
                className="text-xs tracking-wide-luxury text-muted-foreground hover:text-accent transition-colors"
              >
                Already have an account? Sign in
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }



  return (
    <div className="min-h-dvh bg-background text-foreground flex flex-col">
      <header className="px-8 py-6 lg:px-16">
        <Link to="/" className="inline-block">
          <p className="tracking-wide-luxury text-accent">The Hospitality</p>
          <p className="font-display text-lg">Reserve</p>
        </Link>
      </header>

      <main className="flex flex-1 items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-10">
            <p className="tracking-wide-luxury text-accent">
              {mode === "signin" ? "Sign in" : "Create your account"}
            </p>
            <span className="gold-divider mt-6" />
            <h1 className="mt-6 font-display text-3xl">
              {mode === "signin" ? "Welcome back." : "Join The Hospitality Reserve."}
            </h1>
            <p className="mt-3 text-sm text-muted-foreground">
              {mode === "signin"
                ? "Enter your details to continue."
                : "Sign up in under a minute — anyone in hospitality can join."}
            </p>
          </div>


          {shiftPreview && mode === "signup" && (
            <div className="mb-6 border border-accent/40 bg-accent/5 p-4 text-sm">
              <p className="tracking-wide-luxury text-accent text-xs">Registering interest for</p>
              <p className="mt-1 font-medium capitalize">{shiftPreview.role_type.replace(/_/g, " ")} shift</p>
              <p className="text-muted-foreground">
                {new Date(shiftPreview.start_time).toLocaleString("en-GB", {
                  weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
                })}
              </p>
              {shiftPreview.address && (
                <p className="text-muted-foreground">
                  {shiftPreview.address}{shiftPreview.postcode ? ` · ${shiftPreview.postcode}` : ""}
                </p>
              )}
              <p className="text-muted-foreground">£{shiftPreview.hourly_rate.toFixed(2)}/hr</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5 bg-white text-foreground p-8">

            {mode === "signup" && (
              <>
                <div>
                  <Label htmlFor="fullName">Full Name</Label>
                  <Input id="fullName" required value={fullName} onChange={(e) => setFullName(e.target.value)} className="mt-2" />
                </div>
                <div>
                  <Label>Capacity</Label>
                  <RadioGroup value={role} onValueChange={(v) => setRole(v as "bartender" | "venue")} className="mt-2 grid grid-cols-2 gap-2">
                    <label className={`border p-3 cursor-pointer text-sm text-center ${role === "bartender" ? "border-accent bg-accent/5" : "border-border"}`}>
                      <RadioGroupItem value="bartender" className="sr-only" />
                      Bartender
                    </label>
                    <label className={`border p-3 cursor-pointer text-sm text-center ${role === "venue" ? "border-accent bg-accent/5" : "border-border"}`}>
                      <RadioGroupItem value="venue" className="sr-only" />
                      Venue / Caterer
                    </label>
                  </RadioGroup>
                </div>
                {role === "bartender" && (
                  <>
                    <div>
                      <Label htmlFor="postcode">Home Postcode</Label>
                      <Input
                        id="postcode"
                        required
                        value={postcode}
                        onChange={(e) => setPostcode(e.target.value)}
                        placeholder="e.g. SW1A 1AA"
                        autoComplete="postal-code"
                        className="mt-2 uppercase"
                      />
                      <p className="mt-2 text-xs text-muted-foreground">
                        Used to alert you when an engagement is posted within your travel radius.
                      </p>
                    </div>
                    <div>
                      <Label htmlFor="radius">
                        Travel radius — {radius} km
                      </Label>
                      <input
                        id="radius"
                        type="range"
                        min={1}
                        max={100}
                        step={1}
                        value={radius}
                        onChange={(e) => setRadius(Number(e.target.value))}
                        className="mt-2 w-full accent-accent"
                      />
                    </div>
                  </>
                )}
              </>
            )}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                required
                autoComplete="email"
                inputMode="email"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                value={email}
                onChange={(e) => setEmail(e.target.value.replace(/\s+/g, "").toLowerCase())}
                onBlur={(e) => setEmail(e.target.value.replace(/\s+/g, "").toLowerCase())}
                className="mt-2"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                {mode === "signin" && (
                  <Link to="/reset-password" className="text-xs tracking-wide-luxury text-accent hover:text-accent/80 transition-colors">
                    Forgot password?
                  </Link>
                )}
              </div>
              <div className="mt-2">
                <PasswordInput
                  id="password"
                  required
                  minLength={8}
                  autoComplete={mode === "signin" ? "current-password" : "new-password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </div>
            {mode === "signup" && (
              <div>
                <Label htmlFor="referral">Referral code <span className="text-muted-foreground">(optional)</span></Label>
                <Input
                  id="referral"
                  value={referralCodeInput}
                  onChange={(e) => setReferralCodeInput(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 12))}
                  placeholder="e.g. ABC1234"
                  className="mt-2 uppercase tracking-wider"
                />
                <p className="mt-2 text-xs text-muted-foreground">
                  {role === "bartender"
                    ? "Both you and the bartender who referred you get £10 after you complete your first shift."
                    : "Both venues get a free platform-fee waiver (up to 5 workers) after your first completed shift."}
                </p>
              </div>
            )}
            {mode === "signup" && (
              <div className="flex items-start gap-3 border border-border bg-muted/30 p-3">
                <input
                  id="tos"
                  type="checkbox"
                  required
                  checked={tosAccepted}
                  onChange={(e) => setTosAccepted(e.target.checked)}
                  className="mt-1 h-4 w-4 accent-accent"
                />
                <label htmlFor="tos" className="text-xs leading-relaxed text-muted-foreground">
                  I have read and agree to the{" "}
                  <Link to="/legal/terms" target="_blank" className="text-accent underline">Terms of Service</Link>,{" "}
                  <Link to="/legal/privacy" target="_blank" className="text-accent underline">Privacy Policy</Link>
                  {" "}and{" "}
                  <Link to="/legal/cookies" target="_blank" className="text-accent underline">Cookie Policy</Link>
                  {role === "bartender" && (
                    <>
                      , including the{" "}
                      <Link to="/legal/contractor-agreement" target="_blank" className="text-accent underline">Contractor Agreement</Link>
                    </>
                  )}
                  .
                </label>
              </div>
            )}
            <Button type="submit" disabled={loading} className="w-full rounded-none bg-foreground text-background hover:bg-foreground/90 tracking-wide-luxury py-6">
              {loading ? "Please wait…" : mode === "signin" ? "Sign in" : "Create account"}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => setMode(mode === "signin" ? "signup" : "signin")}
              className="text-xs tracking-wide-luxury text-muted-foreground hover:text-accent transition-colors"
            >
              {mode === "signin" ? "New here? Create an account" : "Already have an account? Sign in"}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
